<template>
    <div class="af3-shotcoder-filter-bar-lease-term-container">
        <button 
            class="af3-shotcoder-filter-bar-slider-toggle-button" 
            :aria-expanded="isOpen" 
            @click="toggleDropdown"
            aria-controls="leaseTerm-dropdown"
            aria-haspopup="listbox"
        >
            <span>{{ displaySelectedLeaseTerm }}</span>


            <svg xmlns="http://www.w3.org/2000/svg" width="11.304" height="6.712" viewBox="0 0 11.304 6.712">
                <path id="Path_46144" data-name="Path 46144" d="M9.713,5.652,5.122,1.061.53,5.652-.53,4.591,5.122-1.061l5.652,5.652Z" transform="translate(10.773 5.652) rotate(180)" />
            </svg>



        </button>
        
        <div 
            id="leaseTerm-dropdown"
            class="af3-shotcoder-filter-bar-lease-term-container-content" 
            :aria-hidden="!isOpen"
            v-show="isOpen"
        >
            <ul 
                class="af3-shotcoder-filter-bar-lease-term-options" 
                role="listbox"
                :aria-activedescendant="activeDescendant"
            >
                <li 
                    role="option"
                    id="lease-term-option-any"
                    :aria-selected="effectiveSelected === ''"
                    class="af3-shotcoder-filter-bar-lease-term-option"
                    :class="{ 'is-selected': effectiveSelected === '' }"
                    @click="selectLeaseTerm('')"
                    @keydown.enter="selectLeaseTerm('')"
                    @keydown.space="selectLeaseTerm('')"
                    tabindex="0"
                >
                    Any
                </li>
                <li 
                    v-for="lease_term in available_lease_terms" 
                    :key="lease_term.composite_key"
                    role="option"
                    :id="`lease-term-option-${lease_term.composite_key}`"
                    :aria-selected="effectiveSelected === lease_term.composite_key"
                    class="af3-shotcoder-filter-bar-lease-term-option"
                    :class="{ 'is-selected': effectiveSelected === lease_term.composite_key }"
                    @click="selectLeaseTerm(lease_term.composite_key)"
                    @keydown.enter="selectLeaseTerm(lease_term.composite_key)"
                    @keydown.space="selectLeaseTerm(lease_term.composite_key)"
                    tabindex="0"
                >
                    {{ lease_term.lease_term_name }} ({{ lease_term.start_date }} - {{ lease_term.end_date }})
                </li>
            </ul>
        </div>
    </div>
</template>
  
<script>
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';

export default defineComponent({
    components: { },
   
    props: {
        available_lease_terms: {
            type: Array,
            required: true
        },
        modelValue: {
            type: String,
            default: ''
        }
     },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
        const selectedLeaseTerm = ref(props.modelValue || '');
        const isOpen = ref(false);
        
        // Use prop as source of truth so dropdown pre-fills when parent sets default lease term
        const effectiveSelected = computed(() => (props.modelValue != null && props.modelValue !== '') ? props.modelValue : selectedLeaseTerm.value);
        
        // Watch for prop changes (e.g. parent applying default_lease_term after mount)
        watch(() => props.modelValue, (newValue) => {
            if (newValue !== selectedLeaseTerm.value) {
                selectedLeaseTerm.value = newValue || '';
            }
        }, { immediate: true });
        
        // Compute the display text based on effective selection (prop or local)
        const displaySelectedLeaseTerm = computed(() => {
            const key = effectiveSelected.value;
            if (key === '') {
                return 'Any';
            }
            
            const selected = props.available_lease_terms.find(
                term => term.composite_key === key
            );
            
            return selected 
                ? `${selected.lease_term_name} (${selected.start_date} - ${selected.end_date})`
                : 'Any';
        });
        
        // For accessibility - track the active descendant
        const activeDescendant = computed(() => {
            if (effectiveSelected.value === '') {
                return 'lease-term-option-any';
            }
            return `lease-term-option-${effectiveSelected.value}`;
        });
        
        const toggleDropdown = () => {
            isOpen.value = !isOpen.value;
        };
        
        const selectLeaseTerm = (value) => {
            selectedLeaseTerm.value = value;
            emit('update:modelValue', value);
            isOpen.value = false;
        };
        
        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (isOpen.value && !event.target.closest('.af3-shotcoder-filter-bar-lease-term-container')) {
                isOpen.value = false;
            }
        };
        
        // Handle keyboard navigation
        const handleKeyDown = (event) => {
            if (!isOpen.value) return;
            
            if (event.key === 'Escape') {
                isOpen.value = false;
            }
        };
        
        onMounted(() => {
            document.addEventListener('click', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
        });
        
        onBeforeUnmount(() => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        });

        return {
            selectedLeaseTerm,
            effectiveSelected,
            isOpen,
            displaySelectedLeaseTerm,
            toggleDropdown,
            selectLeaseTerm,
            activeDescendant
        };
    },
});
</script>